import { HoverCard, LoadingOverlay, Text } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { motion } from "framer-motion"
import { Building2, Package } from "lucide-react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useParams } from "react-router-dom"
import {
  CollectionQuery,
  Filter,
  Order,
} from "../../../models/collection.model"
import {
  EntityConfig,
  entityViewMode,
} from "../../../models/entity-config.model"
import { Owner } from "../../../models/owner.model"
import { Property } from "../../../models/property.model"
import EntityList from "../../../shared/component/entity-list/entity-list.component"
import ExportModalComponent from "../../../shared/component/export-modal/component/export-modal.component"
import { useCurrentUser } from "../../../shared/hooks/current-user.hook"
import { dateFormat } from "../../../shared/utility/date-format/date-format"
import StatCard from "../../dashboard/component/stat-cart"
import { exportProperties } from "../api/property.api"
import {
  useBulkTogglePropertyStatusMutation,
  useLazyCountPropertiesQuery,
  useLazyCountUnitsOfPropertyQuery,
  useLazyGetPropertiesQuery,
} from "../store/property.query"

export default function PropertyPage() {
  const params = useParams()
  const location = useLocation()
  const { t } = useTranslation()
  const [viewMode, setViewMode] = useState<entityViewMode>("list")
  const [collection, setCollection] = useState<CollectionQuery>({
    skip: 0,
    top: 10,
    includes: ["units", "owner"],
    orderBy: [{ field: "createdAt", direction: "desc" }],
  })

  const [getProperties, properties] = useLazyGetPropertiesQuery()
  const [countProperties, totalProperties] = useLazyCountPropertiesQuery()
  const [bulkToggleStatus, bulkToggleStatusResponse] =
    useBulkTogglePropertyStatusMutation()
  const [countActiveProperties, activeProperties] =
    useLazyCountPropertiesQuery()
  const [countInactiveProperties, inactiveProperties] =
    useLazyCountPropertiesQuery()
  const [countActiveUnits, activeUnits] = useLazyCountUnitsOfPropertyQuery()
  const [countInactiveUnits, inactiveUnits] = useLazyCountUnitsOfPropertyQuery()
  // const [countUnits, totalUnits] = uselazgetuni();
  const [openedExport, { open: openExport, close: closeExport }] =
    useDisclosure()
  const [userInfo, userId, ownerInfo] = useCurrentUser()
  const generateFilterOption = (value: boolean): Filter[][] => {
    if (userInfo.role.key == "super_admin") {
      return [[{ field: "enabled", value: value, operator: "=" }]]
    }

    if (userInfo?.role?.key == "owner") {
      return [
        [{ field: "enabled", value: value, operator: "=" }],
        [{ field: "ownerId", value: ownerInfo?.parentOwnerId, operator: "=" }],
      ]
    }

    return []

    // return [
    //   [{ field: "enabled", value: value, operator: "=" }],
    //   [{ field: "ownerId", value: userId, operator: "=" }],
    // ]
  }
  useEffect(() => {
    if (userInfo?.role?.key == "owner") {
      getProperties({
        ...collection,
        filter: [
          [
            {
              field: "ownerId",
              value: ownerInfo?.parentOwnerId as string,
              operator: "=",
            },
          ],
        ],
      })
    } else {
      getProperties({
        ...collection,
        filter: [
          [{ field: "owner.confidential", value: false, operator: "=" }],
        ],
      })
    }
  }, [collection])

  useEffect(() => {
    if (userInfo.role.key == "super_admin") {
      countProperties({ count: true })
    } else {
      countProperties({
        count: true,
        filter: [
          [
            {
              field: "ownerId",
              value: ownerInfo?.parentOwnerId as string,
              operator: "=",
            },
          ],
        ],
      })
    }
    countActiveProperties({ count: true, filter: generateFilterOption(true) })
    countInactiveProperties({ filter: generateFilterOption(false) })
    if (userInfo?.role?.key === "owner") {
      countActiveUnits({
        count: true,
        filter: [
          [
            {
              field: "property.ownerId",
              value: ownerInfo?.parentOwnerId,
              operator: "=",
            },
          ],
          [{ field: "enabled", value: true, operator: "=" }],
        ],
      })
      countInactiveUnits({
        filter: [
          [
            {
              field: "property.ownerId",
              value: ownerInfo?.parentOwnerId,
              operator: "=",
            },
          ],
          [{ field: "enabled", value: false, operator: "=" }],
        ],
      })
    }
  }, [])

  useEffect(() => {
    if (params?.id !== undefined) {
      setViewMode("detail")
    } else {
      setViewMode("list")
    }
    if (location.pathname === "/properties/archived") {
      setViewMode("detail")
    }
  }, [setViewMode, params?.id, location])

  const data: Property[] | undefined = properties.data?.data

  const config: EntityConfig<Property> = {
    primaryColumn: {
      key: "name",
      name: t("name"),
      render(value) {
        return (
          <>
            <img
              src={"/building.svg"}
              alt="Product"
              className="size-8 rounded-full"
            />
            {value.name}
          </>
        )
      },
    },
    detailUrl: "detail",
    rootUrl: "/property",
    identity: "id",
    name: "name",
    visibleColumn: [
      {
        key: "name",
        name: t("name"),
        render: (property) => {
          return (
            <Text
              size="sm"
              className={property?.deletedAt ? "text-danger" : ""}
            >
              {property?.name}{" "}
              {userInfo?.role?.key === "super_admin"
                ? `(${property?.owner?.name})`
                : ""}
            </Text>
          )
        },
      },
      { key: "type", name: t("type") },
      {
        key: "totalUnits",
        name: t("totalUnits"),
        render(value: Property) {
          return value?.units?.length ?? 0
        },
      },

      {
        key: "archiveReason",
        name: t("archiveReason"),
        hideSort: true,
        render: (owner: Owner) => {
          if (owner?.archiveReason) {
            return (
              <HoverCard width={280} shadow="md">
                <HoverCard.Target>
                  <Text size="sm" className={"cursor-pointer"}>
                    {owner?.archiveReason && owner?.archiveReason?.length > 25
                      ? owner?.archiveReason?.substring(0, 22) + "..."
                      : owner?.archiveReason}
                  </Text>
                </HoverCard.Target>
                <HoverCard.Dropdown
                  className={
                    "text-justify break-all wrap max-h-60 overflow-auto"
                  }
                >
                  <Text size="sm">{owner?.archiveReason}</Text>
                </HoverCard.Dropdown>
              </HoverCard>
            )
          } else {
            return t("notArchived")
          }
        },
      },
      {
        key: "createdAt",
        name: t("registeredAt"),
        render(value) {
          return dateFormat(value.createdAt)
        },
      },
      {
        key: "enabled",
        name: t("status"),
        render(value) {
          return value.enabled ? t("active") : t("inactive")
        },
      },
    ],
  }

  return (
    // <div className="h-full p-4 mt-16">
    <main className="max-w-7xl mx-auto py-6 px-4 lg:px-8 mt-16">
      <LoadingOverlay visible={properties.isFetching} />
      <motion.div
        className="hidden md:grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
      >
        <StatCard
          name={t("totalProperties")}
          icon={Building2}
          value={totalProperties?.data?.count}
          color="#6366F1"
        />
        <StatCard
          name={t("activeProperties")}
          icon={Building2}
          value={activeProperties?.data?.count}
          color="#8B5CF6"
        />
        <StatCard
          name={t("inactiveProperties")}
          icon={Building2}
          value={inactiveProperties?.data?.count}
          color="#EC4899"
        />

        {userInfo?.role?.key === "owner" && (
          <>
            <StatCard
              name={t("activeUnits")}
              icon={Package}
              value={activeUnits?.data?.count}
              color="#10B981"
            />
            <StatCard
              name={t("deactivatedUnits")}
              icon={Package}
              value={inactiveUnits?.data?.count}
              color="#F59E0B"
            />
          </>
        )}
        {/* <StatCard name='Total Revenue' icon={DollarSign} value='12.5%' color='#10B981' /> */}
      </motion.div>
      <EntityList
        viewMode={viewMode}
        title={t("property", { count: 2 })}
        showExport={true}
        total={properties?.data?.count}
        itemsLoading={properties.isFetching}
        config={config}
        items={data}
        initialPage={1}
        defaultPageSize={10}
        pageSize={[10, 15, 20]}
        collectionQuery={collection}
        onArchivedChecked={(e) =>
          setCollection({
            ...collection,
            withArchived: e,
          })
        }
        onExport={() => openExport()}
        onPaginationChange={(skip: number, top: number) => {
          if (collection.skip !== skip || collection.top !== top) {
            setCollection({ ...collection, skip: skip, top: top })
          }
        }}
        onSearch={(data: any) => {
          setCollection({
            ...collection,
            search: data,
            searchFrom: ["name"],
          })
        }}
        onFilterChange={(data: any) => {
          if (collection?.filter || data.length > 0) {
            setCollection({ ...collection, filter: data })
          }
        }}
        onOrder={(data: Order) =>
          setCollection({ ...collection, orderBy: [data] })
        }
        onBulkActivate={(items) => {
          if (items && items.length > 0)
            bulkToggleStatus({
              ids: items?.map((item) => item.id) as string[],
              enabled: true,
            })
        }}
        onBulkDeactivate={(items) => {
          if (items && items.length > 0)
            bulkToggleStatus({
              ids: items?.map((item) => item.id) as string[],
              enabled: false,
            })
        }}
        bulkActionLoading={bulkToggleStatusResponse.isLoading}
      />

      <ExportModalComponent
        open={openExport}
        opened={openedExport}
        close={closeExport}
        filename={"properties.xlsx"}
        filteringVariables={[
          [
            {
              field: "enabled",
              value: true,
              name: "Active",
              operator: "=",
            },
            {
              field: "enabled",
              value: false,
              name: "Inactive",
              operator: "=",
            },
          ],
          // [
          //   {
          //     field: "gender",
          //     value: "male",
          //     name: "Male",
          //     operator: "=",
          //   },
          //   {
          //     field: "gender",
          //     value: "female",
          //     name: "Female",
          //     operator: "=",
          //   },
          // ],
        ]}
        exportFunction={exportProperties}
      />
    </main>
  )
}
